import React, { useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { Button } from '../../../../components/ui/Button';
import { Field, inputClass } from '../../../../components/ui/Field';
import { PendingButton } from '../../../../components/ui/PendingButton';
import { OperatorSheet } from '../../components/OperatorSheet';
import { QuantityStepper } from '../../components/QuantityStepper';
import { reportShortAtStop, submitRouteShort } from '../../../../features/operatorPicking/operatorPickingRouteThunks';
import { itemPending, shortProblem } from '../../../../features/operatorPicking/operatorPickingRouteUtils';
import { DISCREPANCY_REASON_OPTIONS } from '../../../../features/fulfillment/fulfillmentUtils';
import { formatQuantity } from '../../../../utils/inventoryLabels';

/**
 * «Falta»: finding less of an item than the route says. What was found is
 * said — «Encontré 0 de 4», never assumed —, with the reason chosen from a
 * list and an optional note. It starts on what was counted. Seeded once per
 * opening: remounted by key.
 *
 * @param {object} props
 * @param {boolean} props.isOpen
 * @param {() => void} props.onClose
 * @param {object} props.item - The route's item.
 * @param {string} props.slotCode
 * @param {number} props.counted - Units counted of it, not sent.
 * @param {string} props.routeId
 */
export const RouteShortSheet = ({ isOpen, onClose, item, slotCode, counted, routeId }) => {
  const dispatch = useDispatch();
  const pending = useSelector((state) => state.operatorPickingRoute.pending);
  const [found, setFound] = useState(() => String(counted || 0));
  const [reason, setReason] = useState('');
  const [notes, setNotes] = useState('');
  const left = itemPending(item);
  const problem = shortProblem({ found, reason }, left);
  const foundText = `${formatQuantity(Number(found) || 0)} de ${formatQuantity(left)}`;

  const submit = async () => {
    const action = await dispatch(submitRouteShort(item.key, { found, reason, notes }));
    if (reportShortAtStop.fulfilled.match(action)) onClose();
  };

  return (
    <OperatorSheet
      isOpen={isOpen}
      onClose={onClose}
      title={`Falta ${item.sku} en ${slotCode}`}
      testId="route-short-sheet"
      busy={pending === 'short'}
    >
      <div className="space-y-4">
        <QuantityStepper
          label="¿Cuántas encontraste?"
          value={found}
          onChange={setFound}
          min={0}
          max={Math.max(left - 1, 0)}
          target={left}
          markComplete={false}
          unit={`Encontré ${foundText}`}
          testId="route-short-found"
        />
        <fieldset className="space-y-2">
          <legend className="mb-2 text-sm font-semibold text-ninja-dark dark:text-white">¿Por qué falta?</legend>
          {DISCREPANCY_REASON_OPTIONS.map((option) => (
            <Button
              key={option.value}
              variant="choice"
              aria-pressed={reason === option.value}
              onClick={() => setReason(option.value)}
              data-testid="route-short-reason"
              data-value={option.value}
            >
              <span className="font-semibold">{option.label}</span>
            </Button>
          ))}
        </fieldset>
        <Field label="Nota (opcional)" htmlFor="route-short-notes">
          <input
            id="route-short-notes"
            data-testid="route-short-notes"
            value={notes}
            onChange={(event) => setNotes(event.target.value)}
            autoComplete="off"
            className={`${inputClass} h-12 text-base`}
          />
        </Field>
        <PendingButton
          pendingOn={reportShortAtStop}
          pendingKey={routeId}
          variant="primary"
          size="lg"
          fullWidth
          disabled={Boolean(problem)}
          loadingLabel="Registrando…"
          onClick={submit}
          data-testid="route-short-submit"
        >
          {problem || `Registrar: encontré ${foundText}`}
        </PendingButton>
      </div>
    </OperatorSheet>
  );
};
